/**
 * TrackingPanel — shared shipment tracking timeline view.
 *
 * Props:
 *  - data: {awb, tracking_url, courier_name, courier_code, tracking_status,
 *           tracking_status_raw, tracking_events, tracking_last_sync,
 *           tracking_sync_error}
 *  - onRefresh?: () => Promise<void>   (admin-only, optional)
 *  - showCopyAwb?: boolean              (customer detail page)
 *  - role?: "admin" | "customer"
 *
 * Tidak melakukan fetch sendiri — parent yang load + handle refresh.
 * Konsisten antara halaman admin & customer agar UX seragam.
 */
import React from "react";
import { Truck, RefreshCw, MapPin, XCircle, AlertTriangle, Package, Home, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateTimeID } from "@/lib/dateId";

const STATUS_META = {
  // Customer-facing labels — Bahasa Indonesia, ramah customer
  pending_pickup: { label: "Pesanan Dibuat", color: "bg-gray-100 text-gray-700 border-gray-300", icon: Package },
  picked_up: { label: "Kurir Menjemput", color: "bg-blue-100 text-blue-700 border-blue-300", icon: Truck },
  in_transit: { label: "Dalam Pengiriman", color: "bg-indigo-100 text-indigo-700 border-indigo-300", icon: Truck },
  out_for_delivery: { label: "Sedang Diantar", color: "bg-amber-100 text-amber-700 border-amber-300", icon: Truck },
  delivered: { label: "Pesanan Diterima", color: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: Home },
  cancelled: { label: "Pengiriman Dibatalkan", color: "bg-red-100 text-red-700 border-red-300", icon: XCircle },
  failed: { label: "Pengiriman Gagal", color: "bg-red-100 text-red-700 border-red-300", icon: AlertTriangle },
  returned: { label: "Paket Dikembalikan", color: "bg-orange-100 text-orange-700 border-orange-300", icon: Undo2 },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending_pickup;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${meta.color}`} data-testid={`tracking-status-badge-${status || "unknown"}`}>
      <Icon size={12} /> {meta.label}
    </span>
  );
}

export default function TrackingPanel({ data, onRefresh, showCopyAwb = true, role = "admin" }) {
  const [refreshing, setRefreshing] = React.useState(false);
  const events = data?.tracking_events || [];
  const status = data?.tracking_status;

  const copyAwb = async () => {
    const v = data?.awb;
    if (!v) return;
    try {
      await navigator.clipboard.writeText(v);
      toast.success(`Nomor AWB "${v}" disalin`);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = v;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success(`Nomor AWB "${v}" disalin`); }
      finally { document.body.removeChild(ta); }
    }
  };

  const onRefreshClick = async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try { await onRefresh(); }
    finally { setRefreshing(false); }
  };

  if (!data?.awb) {
    return (
      <div className="text-sm text-[#5C5C5C] italic" data-testid="tracking-panel-no-awb">
        Pesanan belum memiliki nomor AWB. Tracking akan tersedia setelah kurir
        membuat resi pengiriman.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tracking-panel">
      {/* Header: status badge + courier + AWB */}
      <div className="bg-[#FAF9F6] border border-[#E5E5E5] rounded-lg p-4 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <StatusBadge status={status} />
          {onRefresh && (
            <button
              onClick={onRefreshClick}
              disabled={refreshing}
              type="button"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md brand-soft-bg brand-text-primary hover:bg-[#FFD7A8] transition-colors disabled:opacity-50"
              data-testid="tracking-refresh-btn"
            >
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Memperbarui..." : "Refresh Tracking"}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-[#7A5440] uppercase tracking-wider font-semibold text-[10px]">Nomor AWB</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono bg-white border border-[#E5E5E5] rounded px-2 py-0.5" data-testid="tracking-awb">{data.awb}</span>
              {showCopyAwb && (
                <button
                  onClick={copyAwb}
                  type="button"
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded brand-soft-bg brand-text-primary hover:bg-[#FFD7A8] transition-colors"
                  data-testid="tracking-copy-awb-btn"
                >
                  Salin
                </button>
              )}
            </div>
          </div>
          <div>
            <div className="text-[#7A5440] uppercase tracking-wider font-semibold text-[10px]">Kurir</div>
            <div className="mt-0.5">{data.courier_name || data.courier_code || "-"}{data.courier_service ? ` · ${data.courier_service}` : ""}</div>
          </div>
        </div>

        {data.tracking_last_sync && (
          <div className="text-[10px] text-[#5C5C5C] flex items-center gap-2 flex-wrap" data-testid="tracking-last-sync">
            <span>Terakhir disinkronkan: {formatDateTimeID(data.tracking_last_sync)}</span>
            {data.tracking_response_time_ms != null && role === "admin" && (
              <span>· {data.tracking_response_time_ms} ms</span>
            )}
            {data.tracking_last_source && (
              <span
                className={
                  data.tracking_last_source === "webhook"
                    ? "px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-[9px] uppercase tracking-wider"
                    : "px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold text-[9px] uppercase tracking-wider"
                }
                data-testid="tracking-source-badge"
              >
                {data.tracking_last_source === "webhook" ? "via Webhook" : `via ${data.tracking_last_source}`}
              </span>
            )}
          </div>
        )}

        {data.tracking_sync_error && role === "admin" && (
          <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="tracking-sync-error">
            ⚠ Sync error terakhir: {data.tracking_sync_error}
          </div>
        )}

        {data.cancel_reason && (
          <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="tracking-cancel-reason">
            <b>Alasan pembatalan:</b> {data.cancel_reason}
            {data.cancelled_at && (
              <span className="text-[10px] text-red-600 ml-1">
                · {formatDateTimeID(data.cancelled_at)}
              </span>
            )}
          </div>
        )}

        {data.tracking_url && (
          <a href={data.tracking_url} target="_blank" rel="noopener noreferrer"
            className="text-[11px] brand-text-primary hover:underline inline-block"
            data-testid="tracking-url-link"
          >
            🔗 Lihat halaman tracking kurir
          </a>
        )}
      </div>

      {/* Timeline events */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#7A5440] mb-2">Timeline Pengiriman</h3>
        {events.length === 0 ? (
          <div className="text-sm text-[#5C5C5C] italic" data-testid="tracking-timeline-empty">
            Belum ada event tracking. Klik <b>Refresh Tracking</b> untuk update terbaru.
          </div>
        ) : (
          <ol className="relative border-l-2 border-[#FFD7A8] ml-2 space-y-4" data-testid="tracking-timeline">
            {events.slice().reverse().map((ev, idx) => {
              const meta = STATUS_META[ev.status] || STATUS_META.pending_pickup;
              const Icon = meta.icon;
              return (
                <li key={idx} className="ml-4 relative" data-testid={`tracking-event-${idx}`}>
                  <span className={`absolute -left-[26px] top-0.5 w-5 h-5 rounded-full flex items-center justify-center ${idx === 0 ? "brand-bg-primary text-white" : "bg-white border-2 border-[#FFD7A8] brand-text-primary"}`}>
                    <Icon size={11} />
                  </span>
                  <div className="text-xs">
                    <div className="font-semibold text-[#1a1a1a]">{meta.label}</div>
                    {ev.description && <div className="text-[#5C5C5C] mt-0.5">{ev.description}</div>}
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-[#5C5C5C] flex-wrap">
                      {ev.location && (
                        <span className="inline-flex items-center gap-1"><MapPin size={9} /> {ev.location}</span>
                      )}
                      {ev.timestamp && (
                        <span>· {formatDateTimeID(ev.timestamp)}</span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
